import React, { useEffect, useState } from "react";
import { Col, Row } from "antd";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Panel } from "primereact/panel";
import { Password } from "primereact/password";
import Swal from "sweetalert2";

import app from "../../firebase-config";

const DOC_OPTIONS = [
  { label: "DNI", value: "DNI" },
  { label: "Carnet de Extranjeria", value: "Carnet de Extranjeria" },
  { label: "Pasaporte", value: "Pasaporte" },
];

const Registro = ({ open, onClose }) => {
  const [tipoDoc, setTipoDoc] = useState("DNI");
  const [nroDoc, setNroDoc] = useState("");
  const [nombres, setNombres] = useState("");
  const [apellidos, setApellidos] = useState("");
  const [cargo, setCargo] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [empresas, setEmpresas] = useState([]);
  const [nuevaEmpresa, setNuevaEmpresa] = useState("");
  const [errors, setErrors] = useState({});
  const labelStyle = {
    display: "block",
    fontWeight: 700,
    color: "#37474f",
    marginBottom: 6,
  };
  const buttonStyle = { borderRadius: 8 };
  const iconFieldStyle = { display: "block", width: "100%" };
  const panelStyle = { borderRadius: 8, overflow: "hidden" };
  const errorTextStyle = {
    color: "#c62828",
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    lineHeight: "14px",
    minHeight: 14,
    marginTop: 4,
  };

  function resetForm() {
    setTipoDoc("DNI");
    setNroDoc("");
    setNombres("");
    setApellidos("");
    setCargo("");
    setEmpresa(empresas[0] || "");
    setEmail("");
    setPassword("");
    setNuevaEmpresa("");
    setErrors({});
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  function normalizeValue(value) {
    return value?.toString().trim() || "";
  }

  function validateForm() {
    const nextErrors = {};
    const normalizedEmail = normalizeValue(email).toLowerCase();

    if (!tipoDoc) nextErrors.tipoDoc = "Seleccione un tipo de documento.";
    if (!normalizeValue(nroDoc)) nextErrors.nroDoc = "Ingrese el numero de documento.";
    if (tipoDoc === "DNI" && !/^\d{8}$/.test(normalizeValue(nroDoc))) {
      nextErrors.nroDoc = "El DNI debe tener 8 digitos.";
    }
    if (!normalizeValue(nombres)) nextErrors.nombres = "Ingrese nombres.";
    if (!normalizeValue(apellidos)) nextErrors.apellidos = "Ingrese apellidos.";
    if (!normalizeValue(cargo)) nextErrors.cargo = "Ingrese cargo.";
    if (!normalizeValue(empresa)) nextErrors.empresa = "Seleccione una empresa.";
    if (!normalizedEmail) nextErrors.email = "Ingrese correo electronico.";
    if (normalizedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      nextErrors.email = "Ingrese un correo valido.";
    }
    if (!password) nextErrors.password = "Ingrese contrasena.";
    if (password && password.length < 6) {
      nextErrors.password = "La contrasena debe tener al menos 6 caracteres.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function renderError(message) {
    return (
      <small style={{ ...errorTextStyle, visibility: message ? "visible" : "hidden" }}>
        {message || "Sin error"}
      </small>
    );
  }

  function showAlert(options) {
    return Swal.fire({
      ...options,
      didOpen: (popup) => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = "20000";
        if (options.didOpen) options.didOpen(popup);
      },
    });
  }

  function getDni(value) {
    if (tipoDoc !== "DNI" || !/^\d{8}$/.test(value)) return;

    fetch("https://dni.optimizeperu.com/api/persons/" + value)
      .then(async (res) => {
        if (res.status !== 200) return;
        const data = await res.json();
        setNombres(data.name || "");
        setApellidos(`${data.first_name || ""} ${data.last_name || ""}`.trim());
      })
      .catch(() => { });
  }

  async function crearEmpresa() {
    const nombre = normalizeValue(nuevaEmpresa);
    if (!nombre) return;

    const exists = empresas.some((item) => item.toLowerCase() === nombre.toLowerCase());
    if (exists) {
      setEmpresa(nombre);
      setNuevaEmpresa("");
      return;
    }

    try {
      await app.firestore().collection("empresas").add({ nombre });
      setEmpresa(nombre);
      setNuevaEmpresa("");
    } catch (error) {
      showAlert({ title: "Error", text: "No se pudo crear la empresa.", icon: "error" });
    }
  }

  function registrarUsuario(e) {
    e.preventDefault();
    if (!validateForm()) return;

    const crearUsuario = app.functions().httpsCallable("crearUsuario");

    showAlert({
      title: "Procesando registro",
      html: "Espere un momento por favor.",
      didOpen: () => Swal.showLoading(),
      allowOutsideClick: false,
    });

    crearUsuario({
      tipoDoc,
      nroDoc: normalizeValue(nroDoc),
      nombres: normalizeValue(nombres),
      apellidos: normalizeValue(apellidos),
      cargo: normalizeValue(cargo),
      empresa: normalizeValue(empresa),
      email: normalizeValue(email).toLowerCase(),
      password,
      estado: "enabled",
      connected: false,
      tipoUsuario: "Normal",
      token: "",
    })
      .then(() => {
        handleClose();
        showAlert({
          title: "Nuevo Registro",
          text: "El usuario se ha registrado correctamente",
          icon: "success",
        });
      })
      .catch((error) => {
        showAlert({
          title: "Error de Registro",
          text: error.details || error.message || "No se pudo registrar el usuario.",
          icon: "error",
        });
      });
  }

  useEffect(() => {
    const unsubscribe = app
      .firestore()
      .collection("empresas")
      .orderBy("nombre", "asc")
      .onSnapshot((snap) => {
        const lista = snap.docs.map((d) => d.data().nombre).filter(Boolean);
        setEmpresas(lista);
        setEmpresa((current) => current || lista[0] || "");
      });

    return () => unsubscribe();
  }, []);

  const empresaOptions = empresas.map((item) => ({ label: item, value: item }));

  return (
    <Dialog
      visible={open}
      onHide={handleClose}
      header={
        <div className="d-flex align-items-center" style={{ gap: 10 }}>
          <span
            className="d-inline-flex align-items-center justify-content-center"
            style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: "#e8f5e9", color: "#2e7d32" }}
          >
            <i className="pi pi-user-plus"></i>
          </span>
          <span>Registro de Usuario</span>
        </div>
      }
      modal
      draggable={false}
      style={{ width: "860px", maxWidth: "95vw" }}
      contentStyle={{ paddingTop: 12 }}
    >
      <form onSubmit={registrarUsuario}>
        <Panel header="Datos personales" style={panelStyle}>
          <small className="text-muted d-block mb-3">
            Complete los datos requeridos para crear el acceso.
          </small>

          <Row gutter={[14, 14]}>
            <Col xs={24} md={8}>
              <label style={labelStyle}>Tipo doc.</label>
              <Dropdown value={tipoDoc} options={DOC_OPTIONS} onChange={(e) => setTipoDoc(e.value)} className="w-100" />
              {renderError(errors.tipoDoc)}
            </Col>
            <Col xs={24} md={16}>
              <label style={labelStyle}>Numero documento</label>
              <IconField iconPosition="left" style={iconFieldStyle}>
                <InputIcon className="pi pi-id-card" />
                <InputText
                  value={nroDoc}
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    setNroDoc(value);
                    getDni(value);
                  }}
                  className="w-100"
                  placeholder="DNI/CE/Pasaporte"
                />
              </IconField>
              {renderError(errors.nroDoc)}
            </Col>
            <Col xs={24} md={12}>
              <label style={labelStyle}>Nombres</label>
              <IconField iconPosition="left" style={iconFieldStyle}>
                <InputIcon className="pi pi-user" />
                <InputText value={nombres} onChange={(e) => setNombres(e.target.value)} className="w-100" />
              </IconField>
              {renderError(errors.nombres)}
            </Col>
            <Col xs={24} md={12}>
              <label style={labelStyle}>Apellidos</label>
              <IconField iconPosition="left" style={iconFieldStyle}>
                <InputIcon className="pi pi-user" />
                <InputText value={apellidos} onChange={(e) => setApellidos(e.target.value)} className="w-100" />
              </IconField>
              {renderError(errors.apellidos)}
            </Col>
            <Col xs={24} md={12}>
              <label style={labelStyle}>Cargo</label>
              <IconField iconPosition="left" style={iconFieldStyle}>
                <InputIcon className="pi pi-briefcase" />
                <InputText value={cargo} onChange={(e) => setCargo(e.target.value)} className="w-100" />
              </IconField>
              {renderError(errors.cargo)}
            </Col>
            <Col xs={24} md={12}>
              <label style={labelStyle}>Empresa</label>
              <Dropdown
                value={empresa}
                options={empresaOptions}
                onChange={(e) => setEmpresa(e.value)}
                className="w-100"
                placeholder="Seleccione empresa"
              />
              {renderError(errors.empresa)}
            </Col>
            <Col xs={24}>
              <label style={labelStyle}>Crear nueva empresa</label>
              <div className="d-flex" style={{ gap: 8 }}>
                <IconField iconPosition="left" style={iconFieldStyle}>
                  <InputIcon className="pi pi-building" />
                  <InputText
                    value={nuevaEmpresa}
                    onChange={(e) => setNuevaEmpresa(e.target.value)}
                    className="w-100"
                    placeholder="Nombre de empresa"
                  />
                </IconField>
                <Button
                  type="button"
                  icon="pi pi-plus"
                  severity="success"
                  disabled={!nuevaEmpresa.trim()}
                  onClick={crearEmpresa}
                  style={buttonStyle}
                />
              </div>
            </Col>
            <Col xs={24} md={12}>
              <label style={labelStyle}>Correo electronico</label>
              <IconField iconPosition="left" style={iconFieldStyle}>
                <InputIcon className="pi pi-envelope" />
                <InputText value={email} onChange={(e) => setEmail(e.target.value)} className="w-100" />
              </IconField>
              {renderError(errors.email)}
            </Col>
            <Col xs={24} md={12}>
              <label style={labelStyle}>Contrasena</label>
              <Password
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-100"
                inputClassName="w-100"
                feedback={false}
                toggleMask
              />
              {renderError(errors.password)}
            </Col>
          </Row>
        </Panel>

        <div
          className="d-flex justify-content-end mt-4 pt-3"
          style={{ gap: 8, borderTop: "1px solid #e8eef4" }}
        >
          <Button type="button" label="Cancelar" icon="pi pi-times" severity="secondary" outlined onClick={handleClose} style={buttonStyle} />
          <Button type="submit" label="Guardar Usuario" icon="pi pi-save" severity="success" style={buttonStyle} />
        </div>
      </form>
    </Dialog>
  );
};

export default Registro;
